import { GlobalStyles, IconButton, Paper, Stack } from "@mui/material";
import { Panel, Typography } from "@platform/design-system";
import { type RefObject, useCallback, useEffect, useState } from "react";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";

import {
  DRAWER_CONFIG,
  SCENARIO_ACTION_DRAWER_TYPE,
  ScenarioActionDrawerType,
} from "../../constants";
import { CancelModal } from "../ScenarioNotesDrawer/CancelModal";

import { OtherActions } from "./OtherActions";
import {
  AddNewBusinessForm,
  type AddNewBusinessFormHandle,
} from "./AddNewBusinessForm/AddNewBusinessForm";
import { AddNewBusinessFormFooter, FormFooter } from "./Footer";

type ScenarioActionDrawerProps = {
  open: boolean;
  coverageCount?: number;
  drawerType: ScenarioActionDrawerType | null;
  additionalContent?: React.ReactNode;
  isAdditionalContentValid?: boolean;
  onClickClear: () => void;
  onClickConfirm: (
    effectiveDate: Date | null,
    onSuccessCallback: () => void,
    closeDrawer?: boolean,
  ) => void;
  isLoading?: boolean;
  formRef: RefObject<AddNewBusinessFormHandle | null>;
  onClose?: () => void;
};

export const ScenarioActionDrawer: React.FC<ScenarioActionDrawerProps> = ({
  open,
  coverageCount = 0,
  drawerType,
  additionalContent,
  isAdditionalContentValid,
  onClickClear,
  onClickConfirm,
  isLoading,
  onClose,
  formRef,
}) => {
  const [step, setStep] = useState<number>(1);
  const [confirmButtonDisabled, setConfirmButtonDisabled] =
    useState<boolean>(true);
  const [cancelModalOpen, setCancelModalOpen] = useState<boolean>(false);
  const [effectiveDate, setEffectiveDate] = useState<Date | null>(null);

  useEffect(() => {
    setStep(1);
    setConfirmButtonDisabled(true);
    setEffectiveDate(null);
  }, [drawerType]);

  const handleDrawerClose = useCallback((): void => {
    onClose();
    setStep(1);
    setConfirmButtonDisabled(true);
    setEffectiveDate(null);
  }, [onClose]);

  const isOtherActionsConfirmDisabled =
    coverageCount === 0 ||
    !effectiveDate ||
    !(isAdditionalContentValid ?? true);

  const handleCancel = useCallback(() => {
    if (
      formRef.current?.step1FormIsDirty ||
      formRef.current?.step2FormIsDirty
    ) {
      return setCancelModalOpen(true);
    }

    handleDrawerClose();
  }, [handleDrawerClose, formRef]);

  const onClickConfirmDrawer = (): void => {
    onClickConfirm(effectiveDate, handleDrawerClose);
  };

  const handleNext = (): void => {
    setStep((prev) => (prev === 1 ? 2 : 1));
  };

  const handleSaveAndAddAnother = (): void => {
    onClickConfirm(null, () => formRef.current?.resetStep2(), false);
  };

  if (drawerType === null) return;
  const { title, confirmButtonLabel } = DRAWER_CONFIG[drawerType];

  return (
    <>
      {/*
        Hide the JSD "Request Help" launcher while this drawer is open.
        The launcher is pinned to theme.zIndex.appBar (1100), the same level
        as this drawer. Since the drawer is a partial-width right panel rather
        than a full-screen overlay, raising its z-index only partially covers
        the launcher, which looks broken — so we hide it entirely instead.
      */}
      {open && (
        <GlobalStyles
          styles={{ "#jsd-widget": { display: "none !important" } }}
        />
      )}
      <Panel
        open={open}
        anchor="right"
        sx={{
          position: "absolute",
          right: "-44px",
          top: "-24px",
          bottom: "-24px",
        }}
      >
        <Stack direction="column" spacing={0} sx={{ height: "100%" }}>
          <Stack
            p={2}
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="body-large" color="textSecondary">
              {title}
            </Typography>

            <IconButton
              size="medium"
              onClick={() => handleCancel()}
              aria-label="close-drawer"
            >
              <CloseOutlinedIcon />
            </IconButton>
          </Stack>

          <Paper
            sx={{
              p: 2,
              flex: 1,
              minHeight: 0,
              overflowY: "auto",
              borderLeft: 0,
              borderRight: 0,
              borderRadius: 0,
            }}
            variant="outlined"
          >
            {drawerType === SCENARIO_ACTION_DRAWER_TYPE.AddNew ? (
              <AddNewBusinessForm
                step={step}
                setConfirmButtonDisabled={setConfirmButtonDisabled}
                ref={formRef}
              />
            ) : (
              <OtherActions
                additionalContent={additionalContent}
                onClickClear={onClickClear}
                coverageCount={coverageCount}
                effectiveDate={effectiveDate}
                onEffectiveDateChange={setEffectiveDate}
              />
            )}
          </Paper>

          <Stack
            direction="row"
            justifyContent="flex-end"
            spacing={2}
            px={2}
            py={1}
            mt="auto"
          >
            {drawerType === SCENARIO_ACTION_DRAWER_TYPE.AddNew ? (
              <AddNewBusinessFormFooter
                step={step}
                confirmButtonLabel={confirmButtonLabel}
                onCloseDrawer={handleCancel}
                onNext={handleNext}
                onSave={onClickConfirmDrawer}
                onSaveAndAddAnother={handleSaveAndAddAnother}
                confirmButtonDisabled={confirmButtonDisabled || isLoading}
              />
            ) : (
              <FormFooter
                confirmButtonLabel={confirmButtonLabel}
                onClickConfirm={onClickConfirmDrawer}
                disabled={isOtherActionsConfirmDisabled || isLoading}
                onCloseDrawer={handleDrawerClose}
              />
            )}
          </Stack>
        </Stack>

        <CancelModal
          open={cancelModalOpen}
          setOpen={setCancelModalOpen}
          confirmChange={handleDrawerClose}
        />
      </Panel>
    </>
  );
};
